import { z } from "zod";
import { MODEL_TIERS, ModelTierSchema } from "./agent.js";
/**
 * Reasoning effort levels, exactly the Claude Agent SDK's `EffortLevel` (`options.effort`,
 * verified in 0.3.316: 'low' | 'medium' | 'high' | 'xhigh' | 'max'). Absent = the model default.
 */
export const EFFORT_LEVELS = ["low", "medium", "high", "xhigh", "max"];
export const EffortLevelSchema = z.enum(EFFORT_LEVELS);
/** A model id, a tier name, or "auto" (the orchestrator decides). */
export const ModelChoiceSchema = z.string().trim().min(1).max(100);
/**
 * What a user (assistant setting) or one message asks for. Both fields optional; omitted or
 * "auto" means "let policy decide". `model` is a tier (`fast|standard|strong`) or a model id.
 */
export const ModelPreferenceSchema = z.object({
    model: ModelChoiceSchema.optional(),
    effort: EffortLevelSchema.optional(),
});
/**
 * Per-connection model policy (owner panel). Every field is optional so old rows stay valid;
 * absent = no restriction / engine defaults. Monthly budget lives in `Connection.budget`.
 */
export const ConnectionModelPolicySchema = z.object({
    /** Models this connection may run. Absent or empty = all configured models. */
    allowedModels: z.array(z.string().min(1).max(100)).max(50).optional(),
    /** Used when the requested model is not allowed here (must be in `allowedModels`). */
    defaultModel: z.string().min(1).max(100).optional(),
    /** Default reasoning effort for runs on this connection. */
    effort: EffortLevelSchema.optional(),
    /** Most runs this connection serves at once. Absent = unlimited. */
    maxConcurrent: z.number().int().min(1).max(1000).optional(),
    /** Daily spend cap in USD (real money, or notional when the budget counts it). Absent = none. */
    dailyLimitUsd: z.number().nonnegative().optional(),
    /** Free-text note, e.g. the subscription plan's tier limits. */
    planNote: z.string().max(500).optional(),
});
export const EffortSettingSchema = z.enum(["auto", ...EFFORT_LEVELS]);
/**
 * Per-company model policy (owner panel; stored as `engine.model.*` settings, see
 * `@uzverse/orchestrator` `MODEL_SETTINGS`). Empty model id = the engine config's tier model.
 */
export const CompanyModelPolicySchema = z.object({
    tiers: z.object({
        fast: z.string().max(100),
        standard: z.string().max(100),
        strong: z.string().max(100),
    }),
    effort: z.object({
        fast: EffortSettingSchema,
        standard: EffortSettingSchema,
        strong: EffortSettingSchema,
    }),
    /** Highest tier background agents (leads, workers) may use. */
    workerMaxTier: ModelTierSchema,
    /** Highest tier the user's assistant may use. */
    assistantMaxTier: ModelTierSchema,
    /** May users choose their assistant's model/effort? */
    allowAssistantChoice: z.boolean(),
    /** May a single message override model/effort? */
    allowMessageOverride: z.boolean(),
    benchmarkHint: z.string().max(1000),
});
export const DEFAULT_COMPANY_MODEL_POLICY = {
    tiers: { fast: "", standard: "", strong: "" },
    effort: { fast: "auto", standard: "auto", strong: "auto" },
    workerMaxTier: "strong",
    assistantMaxTier: "strong",
    allowAssistantChoice: true,
    allowMessageOverride: true,
    benchmarkHint: "",
};
/** Position of a tier, cheapest first. */
export const tierRank = (tier) => MODEL_TIERS.indexOf(tier);
/** Setting keys that store the model policy (company scope) and the assistant choice (user scope). */
export const MODEL_SETTING_KEYS = {
    tierFast: "engine.model.tier.fast",
    tierStandard: "engine.model.tier.standard",
    tierStrong: "engine.model.tier.strong",
    effortFast: "engine.model.effort.fast",
    effortStandard: "engine.model.effort.standard",
    effortStrong: "engine.model.effort.strong",
    workerMaxTier: "engine.model.workerMaxTier",
    assistantMaxTier: "engine.model.assistantMaxTier",
    allowAssistantChoice: "engine.model.allowAssistantChoice",
    allowMessageOverride: "engine.model.allowMessageOverride",
    benchmarkHint: "engine.model.benchmarkHint",
    /** User scope: "auto", a tier or a model id. */
    assistantModel: "engine.assistant.model",
    /** User scope: "auto" or an effort level. */
    assistantEffort: "engine.assistant.effort",
};
/** The company policy as flat setting values. */
export function companyPolicyToValues(p) {
    const k = MODEL_SETTING_KEYS;
    return {
        [k.tierFast]: p.tiers.fast,
        [k.tierStandard]: p.tiers.standard,
        [k.tierStrong]: p.tiers.strong,
        [k.effortFast]: p.effort.fast,
        [k.effortStandard]: p.effort.standard,
        [k.effortStrong]: p.effort.strong,
        [k.workerMaxTier]: p.workerMaxTier,
        [k.assistantMaxTier]: p.assistantMaxTier,
        [k.allowAssistantChoice]: p.allowAssistantChoice,
        [k.allowMessageOverride]: p.allowMessageOverride,
        [k.benchmarkHint]: p.benchmarkHint,
    };
}
/** Builds the policy from flat setting values; missing or invalid ones take the defaults. */
export function companyPolicyFromValues(get) {
    const k = MODEL_SETTING_KEYS;
    const d = DEFAULT_COMPANY_MODEL_POLICY;
    const str = (key, fallback) => {
        const v = get(key);
        return typeof v === "string" ? v : fallback;
    };
    const bool = (key, fallback) => {
        const v = get(key);
        return typeof v === "boolean" ? v : fallback;
    };
    const effort = (key, fallback) => {
        const r = EffortSettingSchema.safeParse(get(key));
        return r.success ? r.data : fallback;
    };
    const tier = (key, fallback) => {
        const r = ModelTierSchema.safeParse(get(key));
        return r.success ? r.data : fallback;
    };
    return {
        tiers: {
            fast: str(k.tierFast, d.tiers.fast),
            standard: str(k.tierStandard, d.tiers.standard),
            strong: str(k.tierStrong, d.tiers.strong),
        },
        effort: {
            fast: effort(k.effortFast, d.effort.fast),
            standard: effort(k.effortStandard, d.effort.standard),
            strong: effort(k.effortStrong, d.effort.strong),
        },
        workerMaxTier: tier(k.workerMaxTier, d.workerMaxTier),
        assistantMaxTier: tier(k.assistantMaxTier, d.assistantMaxTier),
        allowAssistantChoice: bool(k.allowAssistantChoice, d.allowAssistantChoice),
        allowMessageOverride: bool(k.allowMessageOverride, d.allowMessageOverride),
        benchmarkHint: str(k.benchmarkHint, d.benchmarkHint),
    };
}
//# sourceMappingURL=model-policy.js.map